import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import {
  DATA_TABLE_SOURCE_USER_BOOKMARKS,
  DATA_TABLE_VIEW_ID_USER_BOOKMARKS_DEFAULT,
} from 'common/dataTableConstants';
import {
  TOUR_SHOWING_APPLICATION_SOURCE_PAGE_BOOOKMARKED,
} from 'common/tourConstants';

import {
  USER_ROLE_RENTER,
  USER_ROLE_ANONYMOUS,
} from 'common/userRoles';
import { SCREEN_ID_BOOKMARKS } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';

import Navbar from 'client/components/Navbar';
import TabBar from 'client/components/TabBar';
import ListingsListViewV2 from 'client/components/ListingsListViewV2';
import NoBookmarks from 'client/components/NoBookmarks';
import SrContinueApplicationButton from 'client/components/SrContinueApplicationButton';

import branch from 'client/core/branch';

import UserActions from 'client/actions/User';

import {
  dataTable,
  actionDataTableLoadItems,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

function getDataTableSettings({ currentUser }) {
  return {
    dataSource: DATA_TABLE_SOURCE_USER_BOOKMARKS,
    viewId: DATA_TABLE_VIEW_ID_USER_BOOKMARKS_DEFAULT,
    count: 10000,
    sort: {
      bookmarkCreatedDate: 'desc',
    },
    filter: {
      'userBookmarks.userId': currentUser.id,
    },
  };
}

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_RENTER,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_BOOKMARKS)
@withScrollToTop
@branch({
  currentUser: ['currentUser'],
  householdApplications: ['householdApplications'],
  newBookmarksCount: ['newBookmarksCount'],
  bookmarksByListingId: ['bookmarksByListingId'],
  listingPhotoResolution: ['listingPhotoResolution'],
})
@dataTable(({ currentUser }) => ({
  dataSource: DATA_TABLE_SOURCE_USER_BOOKMARKS,
  viewId: DATA_TABLE_VIEW_ID_USER_BOOKMARKS_DEFAULT,
  sort: {
    bookmarkCreatedDate: 'desc',
  },
  filter: {
    'userBookmarks.userId': currentUser.id,
  },
}))
class BookmarksPage extends Component {
  static fetchData(match, inject) {
    const currentUser = inject.state.get(['currentUser']);

    const dataTableIsFullyLoaded = inject.state.get([
      'dataTables',
      DATA_TABLE_SOURCE_USER_BOOKMARKS,
      'views',
      DATA_TABLE_VIEW_ID_USER_BOOKMARKS_DEFAULT,
      'isFullyLoaded',
    ]);

    if (!dataTableIsFullyLoaded) {
      const dataTableSettings = getDataTableSettings({ currentUser });
      actionInitializeDataTableState(inject, dataTableSettings);
      return actionDataTableLoadItems(inject, dataTableSettings);
    }
  }

  static getPageMetadata = () => ({
    pageTitle: 'Saved | Sunroom Rentals',
    section: 'Bookmarks',
    keywords: 'sunroom bookmarks, sunroom',
    description: 'Your saved listings.',
  })

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    householdApplications: PropTypes.array.isRequired,
    listingPhotoResolution: PropTypes.number.isRequired,
    items: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
  }

  componentDidMount() {
    const {
      newBookmarksCount,
      dispatch,
    } = this.props;

    if (newBookmarksCount) {
      dispatch(UserActions.resetNewBookmarksCount);
    }
  }

  _onBookmarkClick = ({ listing, isBookmarked }) => {
    const { currentUser, dispatch } = this.props;
    Promise.resolve()
      .then(() => {
        return isBookmarked
          ? dispatch(UserActions.removeBookmark, { listingId: listing.id })
          : dispatch(UserActions.addBookmark, { listingId: listing.id });
      })
      .then(() => {
        return dispatch(actionDataTableLoadItems, getDataTableSettings({ currentUser }));
      });
  }

  render() {
    const {
      items,
      dataTableApiState,
      bookmarksByListingId,
      currentUser,
      householdApplications,
      listingPhotoResolution,
    } = this.props;

    const bookmarksCount = Object.keys(bookmarksByListingId).length;

    return (
      <div className='page-bookmarks'>
        <Navbar />
        {!currentUser.roles.includes(USER_ROLE_ANONYMOUS) && (
          <SrContinueApplicationButton />
        )}
        <TabBar />

        {bookmarksCount ? null : <NoBookmarks />}

        <div className='bookmarksContainer clearfix'>
          {(!bookmarksCount ? null : (
            <div>
              <span className='headline main'>{pluralize('saved rental', bookmarksCount, true)}</span>
              <ListingsListViewV2
                savePositionWithId={SCREEN_ID_BOOKMARKS}
                listings={items}
                isLoading={dataTableApiState.inProgress}
                // onLoadMore={this._onLoadMore}
                bookmarksByListingId={bookmarksByListingId || {}}
                onBookmarkClick={this._onBookmarkClick}
                currentUser={currentUser}
                householdApplications={householdApplications}
                photoResolution={listingPhotoResolution}
                showRequestShowingButton
                requestedFrom={TOUR_SHOWING_APPLICATION_SOURCE_PAGE_BOOOKMARKED}
              />
              {/*
                TODO: Handle the following states.
                {dataTableApiState.inProgress && (
                  <div>LOADING</div>
                )}
                {dataTableApiState.error && (
                  <div>ERROR: { dataTableApiState.error }</div>
                )}
              */}
            </div>
          ))}
        </div>
      </div>
    );
  }
}

BookmarksPage.displayName = 'BookmarksPage';

export default BookmarksPage;
